import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./orderDrawer.module.css";
import { getOrderItems, getProcessingItems } from "../../api/orderBoard";
import { DownloadFile } from "../../api/core";
import OrderItemView from "./OrderItemView";

const deliveryTypes = {
  1: "Самовывоз",
  2: "Курьер",
  3: "СДЭК",
  4: "Почта России",
};

export default function OrderDrawer({ order, jwt, onClose, orderClose }) {
  const [processItems, setProcessItems] = useState([]);
  const [orderItems, setOrderItems] = useState([]);

  const isOpen = Boolean(order);
  
  const date = useMemo(() => {
    if (!order?.dateCreated) return "—";

    return new Date(order.dateCreated).toLocaleDateString("ru-RU", {
      day: "numeric",
      month: "long",
      year: "2-digit",
    });
  }, [order]);

  useEffect(() => {
    if (!order?.id) {
      setProcessItems([]);
      setOrderItems([]);
      return;
    }

    getOrderItems(order.id, jwt)
      .then((response) => response.json())
      .then((data) => setOrderItems(data ?? []))
      .catch(console.log);

    getProcessingItems(order.id, jwt)
      .then((response) => response.json())
      .then((data) => setProcessItems(data ?? []))
      .catch(console.log);
  }, [order?.id, jwt]);

  const orderClosePreview = useCallback(() => {
    if (!order?.id) return;

    if (window.confirm("Вы действительно хотите завершить заказ?")) {
      orderClose(order.id);
    }
  }, [order, orderClose]);

  const downloadSide = async (itemId, sideId, type) => {
    await DownloadFile(
      `/api/medals/v1/drafts/${itemId}/sides/${sideId}/${type}`,
      "GET",
      jwt,
      undefined,
      `medal-${itemId}-${sideId}.${type}`
    );
  };

  if (!order) {
    return null;
  }

  return (
    <>
      <div
        className={`${styles.overlay} ${isOpen ? styles.overlayActive : ""}`}
        onClick={onClose}
      />

      <aside
        className={`${styles.drawer} ${isOpen ? styles.drawerActive : ""}`}
        aria-hidden={!isOpen}
      >
        <div className={styles.drawerHeader}>
          <div className={styles.drawerHeaderMain}>
            <div className={styles.drawerId}>Заказ #{order.id}</div>
            <div className={styles.drawerDate}>{date}</div>
          </div>

          <div className={styles.drawerActions}>
            <button
              type="button"
              className={styles.closeOrderButton}
              onClick={orderClosePreview}
            >
              Завершить заказ
            </button>

            <button
              type="button"
              className={styles.drawerCloseButton}
              onClick={onClose}
              aria-label="Закрыть"
            >
              ✕
            </button>
          </div>
        </div>

        <div className={styles.drawerBody}>
          <div className={styles.formGrid}>
            <InfoField label="Дата заказа" value={date} />
            <InfoField label="Получатель" value={order.receiver} />
            <InfoField label="Номер телефона" value={order.phone} />
            <InfoField label="Цена" value={`${Number(order.price).toFixed(0)} ₽`} />
            <InfoField label="Адрес" value={order.address} />
            <InfoField
              label="Доставка"
              value={deliveryTypes[order.wayDelivery] || "—"}
            />

            {order.deliveryDescription ? (
              <div className={styles.formGroupFull}>
                <div className={styles.fieldLabel}>Описание доставки</div>
                <div className={styles.fieldValueMultiline}>
                  {order.deliveryDescription}
                </div>
              </div>
            ) : null}
          </div>

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Этапы</h4>

            <div className={styles.timeline}>
              {processItems.length > 0 ? (
                processItems.map((item) => (
                  <div key={item.id} className={styles.timelineItem}>
                    <div className={styles.timelinePoint} />
                    <div className={styles.timelineContent}>
                      <div className={styles.timelineMessage}>{item.message}</div>
                      <div className={styles.timelineDate}>
                        {new Date(item.dateCreated).toLocaleDateString("ru-RU", {
                          day: "numeric",
                          month: "long",
                          year: "2-digit",
                        })}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.emptyBlock}>Этапы пока отсутствуют</div>
              )}
            </div>
          </section>

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Товары</h4>

            <div className={styles.items}>
              {orderItems.length > 0 ? (
                orderItems.map((item) => (
                  <OrderItemView
                    key={item.id}
                    item={item}
                    downloadSide={downloadSide}
                    jwt={jwt}
                  />
                ))
              ) : (
                <div className={styles.emptyBlock}>Товары пока не загружены</div>
              )}
            </div>
          </section>
        </div>
      </aside>
    </>
  );
}

function InfoField({ label, value }) {
  return (
    <div className={styles.formGroup}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{value || "—"}</div>
    </div>
  );
}